import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useCurrentUser } from '@/app/providers/user';

/**
 * Sign out, then land on the public page — the one way the app does this.
 *
 * Two copies of this used to exist, byte-identical, in the rail's user chip and on
 * the account page. They had already drifted once: the account page still sent you
 * to `/login`, which was the only signed-out destination when it was written, so
 * signing out asked you to sign straight back in.
 *
 * The ordering is the part worth keeping together:
 *
 * `finally`, not `then` — `logout()` clears local state whether or not the server
 * call succeeds, so a viewer who asked to leave has left, and the destination must
 * not depend on the network. On `then` alone a failed request stranded them on a
 * page with no session behind it.
 *
 * `catch` before it, because `finally` re-raises whatever it was handed. Without
 * one, an unexpected throw would navigate AND leave an unhandled rejection.
 *
 * The catch reports rather than swallowing. `logout()` already absorbs network
 * failure internally, so anything arriving here is a genuine bug rather than the
 * expected offline case — discarding it silently would hide a real regression in
 * production, where this path is hard to observe.
 */
export function useLogoutRedirect(): () => void {
  const { logout } = useCurrentUser();
  const navigate = useNavigate();

  return useCallback(() => {
    void logout()
      .catch((error: unknown) => {
        Sentry.captureException(error, { tags: { action: 'logout' } });
      })
      .finally(() => navigate('/', { replace: true }));
  }, [logout, navigate]);
}
